import React, { useState, useEffect } from "react";
import {
  fetchLeadList,
  updateLead,
  deleteLead,
  togglePinConversation,
  deleteConversation,
  downloadAudioFile,
  createUserLead,
  fetchAllAgents,
  getLeadAgents,
  addAgentToLead,
  removeAgentFromLead,
  Lead,
  LeadListResponse,
  LeadSignup,
  Conversation,
  AgentSummary,
  LeadAgentAccess,
} from "../../services/api";
import "./Admin.css";
import StereoAudioVisualizer from "../Common/AudioVisualizer/AudioVisualizer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faDownload,
  faTimes,
  faCopy,
  faKey,
  faLink,
  faUnlink,
} from "@fortawesome/free-solid-svg-icons";
import {
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePhone,
  HiMail,
  HiOfficeBuilding,
  HiClipboardCheck,
  HiStar,
  HiOutlineStar,
  HiPlay,
  HiDownload,
  HiUserAdd,
  HiClipboard,
  HiX,
  HiLockClosed,
  HiPlus,
} from "react-icons/hi";
import ConfirmationModal from "../Common/ConfirmationModal";
import LoadingState from "../Common/LoadingState";

// Add CSS for copied tooltip animation
const copiedTooltipStyle = `
  @keyframes fadeInOut {
    0% { opacity: 0; transform: translate(-50%, 10px); }
    20% { opacity: 1; transform: translate(-50%, 0); }
    80% { opacity: 1; transform: translate(-50%, 0); }
    100% { opacity: 0; transform: translate(-50%, -10px); }
  }
  .copied-tooltip {
    animation: fadeInOut 1.5s ease-in-out forwards;
  }
`;

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [showCreateUserModal, setShowCreateUserModal] =
    useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [maxUses, setMaxUses] = useState<number>(5);
  const [expandedLead, setExpandedLead] = useState<number | null>(null);
  const [expandedConversation, setExpandedConversation] = useState<
    string | null
  >(null);

  // New state for agent access management
  const [showAddAgentModal, setShowAddAgentModal] = useState<boolean>(false);
  const [availableAgents, setAvailableAgents] = useState<AgentSummary[]>([]);
  const [selectedAgentUuid, setSelectedAgentUuid] = useState<string>("");
  const [loadingAgents, setLoadingAgents] = useState<boolean>(false);
  const [showAgentAccess, setShowAgentAccess] = useState<number | null>(null);
  const [confirmRemoveAgentId, setConfirmRemoveAgentId] = useState<
    string | null
  >(null);

  // New state for lead agents
  const [leadAgents, setLeadAgents] = useState<{
    [leadId: number]: LeadAgentAccess[];
  }>({});

  // New state for user creation form
  const [newUser, setNewUser] = useState<LeadSignup>({
    max_uses: 5,
    fullname: "",
    email: "",
    phone: "",
    company: "",
  });

  // Audio playback states
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [audioLoaded, setAudioLoaded] = useState<boolean>(false);
  const [audioLoading, setAudioLoading] = useState<boolean>(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  // Copied Lead notification
  const [copiedLead, setCopiedLead] = useState<string | null>(null);

  // Confirmation modal states
  const [showDeleteLeadModal, setShowDeleteLeadModal] =
    useState<boolean>(false);
  const [leadToDelete, setLeadToDelete] = useState<string>("");
  const [showDeleteConversationModal, setShowDeleteConversationModal] =
    useState<boolean>(false);
  const [conversationToDelete, setConversationToDelete] = useState<string>("");

  // Add state for the token copy animation
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const loadLeads = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Calculate the correct offset based on the current page
      const offset = (currentPage - 1) * 10;
      const limit = 10;

      const result = await fetchLeadList(limit, offset);
      setLeads(result.data);
      setTotalPages(
        Math.ceil(result.pagination.total / result.pagination.limit)
      );
    } catch (err: any) {
      console.error("Error loading leads:", err);
      setError(err.message || "Failed to load leads");
      setLeads([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  // Load agents for a specific lead
  const loadLeadAgents = async (leadId: number) => {
    try {
      const agents = await getLeadAgents(leadId.toString());
      setLeadAgents((prev) => ({
        ...prev,
        [leadId]: agents,
      }));
    } catch (error: any) {
      console.error(`Error loading agents for lead ${leadId}:`, error);
      setError(`Failed to load agents for lead: ${error.message}`);
    }
  };

  // Load available agents for the add agent modal
  const loadAvailableAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await fetchAllAgents(50, 0); // Get up to 50 agents
      // Filter out public and disabled agents
      const filteredAgents = response.data.filter(
        (agent) => !agent.is_public && agent.enabled
      );

      // Also filter out agents that the lead already has access to
      if (selectedLeadId && leadAgents[parseInt(selectedLeadId)]) {
        const existingAgentIds = leadAgents[parseInt(selectedLeadId)].map(
          (agent) => agent.uuid
        );
        const availableAgents = filteredAgents.filter(
          (agent) => !existingAgentIds.includes(agent.uuid)
        );
        setAvailableAgents(availableAgents);
      } else {
        setAvailableAgents(filteredAgents);
      }
    } catch (error: any) {
      console.error("Error loading available agents:", error);
      setError(`Failed to load available agents: ${error.message}`);
    } finally {
      setLoadingAgents(false);
    }
  };

  // Open add agent modal for a specific lead
  const openAddAgentModal = async (leadId: number) => {
    setSelectedLeadId(leadId.toString());
    // Reset the selected agent when opening modal for any lead
    setSelectedAgentUuid("");

    // Ensure we have the lead agents loaded before opening the modal
    if (!leadAgents[leadId]) {
      await loadLeadAgents(leadId);
    }

    loadAvailableAgents();
    setShowAddAgentModal(true);
  };

  // Add agent access to a lead
  const handleAddAgentAccess = async () => {
    if (!selectedLeadId || !selectedAgentUuid) {
      setError("Please select an agent to add");
      return;
    }

    try {
      const result = await addAgentToLead(selectedLeadId, selectedAgentUuid);
      if (result.success) {
        // Reload agents for this lead
        await loadLeadAgents(parseInt(selectedLeadId));
        setShowAddAgentModal(false);
        // Make sure to reset selected agent after adding
        setSelectedAgentUuid("");
      } else {
        setError(result.error || "Failed to add agent access");
      }
    } catch (error: any) {
      setError(`Failed to add agent access: ${error.message}`);
    }
  };

  // Remove agent access from a lead
  const handleRemoveAgentAccess = async (leadId: number, agentUuid: string) => {
    try {
      const result = await removeAgentFromLead(leadId.toString(), agentUuid);
      if (result.success) {
        // Reload agents for this lead
        await loadLeadAgents(leadId);
        setConfirmRemoveAgentId(null);
      } else {
        setError(result.error || "Failed to remove agent access");
      }
    } catch (error: any) {
      setError(`Failed to remove agent access: ${error.message}`);
    }
  };

  // Toggle agent access view
  const toggleAgentAccess = (leadId: number) => {
    // Close expanded conversation when opening agent access
    setExpandedConversation(null);

    if (showAgentAccess === leadId) {
      setShowAgentAccess(null);
    } else {
      setShowAgentAccess(leadId);
      // Also close any expanded lead
      if (expandedLead === leadId) {
        setExpandedLead(null);
      }
      // Load agents for this lead if not already loaded
      if (!leadAgents[leadId]) {
        loadLeadAgents(leadId);
      }
    }
  };

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Clean up audio URL when component unmounts
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Clean up when modal is closed
  useEffect(() => {
    if (!showModal) {
      setIsPlaying(false);
      setAudioLoaded(false);
      setAudioError(null);
    }
  }, [showModal]);

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleUserInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({
      ...prev,
      [name]: name === "max_uses" ? Math.max(1, Number(value)) : value,
    }));
  };

  const handleCreateUser = async () => {
    try {
      // Convert empty strings to defined values to avoid backend validation errors
      const userData: LeadSignup = {
        max_uses: newUser.max_uses || 5,
        fullname: newUser.fullname || "",
        email: newUser.email || "",
        phone: newUser.phone || "",
        company: newUser.company || "",
      };

      const result = await createUserLead(userData);
      if (result.success) {
        setShowCreateUserModal(false);
        // Reset form
        setNewUser({
          max_uses: 5,
          fullname: "",
          email: "",
          phone: "",
          company: "",
        });
        loadLeads();
      } else {
        setError(result.error || "Failed to create user");
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateClick = (leadId: string, currentMaxUses: number) => {
    setSelectedLeadId(leadId);
    setMaxUses(currentMaxUses);
    setShowUpdateModal(true);
  };

  const handleUpdate = async () => {
    try {
      await updateLead(selectedLeadId, maxUses);
      loadLeads();
      setShowUpdateModal(false);
    } catch (err: any) {
      setError(`Failed to update lead: ${err.message}`);
    }
  };

  const handleDelete = async (leadId: string) => {
    try {
      await deleteLead(leadId);
      loadLeads();
      setShowDeleteLeadModal(false);
    } catch (err: any) {
      setError(`Failed to delete lead: ${err.message}`);
    }
  };

  const openDeleteLeadModal = (leadId: string) => {
    setLeadToDelete(leadId);
    setShowDeleteLeadModal(true);
  };

  const toggleLeadExpand = (leadId: number) => {
    if (expandedLead === leadId) {
      setExpandedLead(null);
    } else {
      setExpandedLead(leadId);
    }
    // Close any expanded conversation when toggling Lead
    setExpandedConversation(null);
  };

  const toggleConversationExpand = (convId: string) => {
    if (expandedConversation === convId) {
      setExpandedConversation(null);
    } else {
      setExpandedConversation(convId);
    }
  };

  const generateConversationId = (conv: Conversation): string => {
    // For admin view conversations that have an ID, use that
    if (conv.id) {
      return conv.id.toString();
    }
    // For Lead user conversations that don't have an ID, generate a unique identifier
    return `${conv.lead_id}-${conv.agent_id}-${new Date(
      conv.timestamp
    ).getTime()}`;
  };

  const handlePinConversation = async (
    conversationId: string,
    isPinned: boolean
  ) => {
    try {
      // Extract the actual ID if it exists (admin view), otherwise use the composite ID (Lead view)
      const actualId = conversationId.includes("-")
        ? conversationId
        : conversationId;
      await togglePinConversation(actualId);
      // Reload Leads to refresh conversation pin status
      loadLeads();
    } catch (err: any) {
      setError(
        `Failed to ${isPinned ? "unpin" : "pin"} conversation: ${err.message}`
      );
    }
  };

  const openDeleteConversationModal = (conversationId: string) => {
    setConversationToDelete(conversationId);
    setShowDeleteConversationModal(true);
  };

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      // Extract the actual ID if it exists (admin view), otherwise use the composite ID (Lead view)
      const actualId = conversationId.includes("-")
        ? conversationId
        : conversationId;
      await deleteConversation(actualId);
      loadLeads();
      setShowDeleteConversationModal(false);
    } catch (err: any) {
      setError(`Failed to delete conversation: ${err.message}`);
    }
  };

  // Handle play button click
  const handlePlay = async (conversation: Conversation) => {
    if (!conversation.recording_filename) {
      setError("No recording available for this conversation");
      return;
    }

    try {
      // Reset error state at the start
      setAudioError(null);

      // If we're already showing this conversation, just toggle play state
      if (selectedConversation?.id === conversation.id && audioLoaded) {
        setIsPlaying(!isPlaying);
        setShowModal(true);
        return;
      }

      // Stop any current playback
      setIsPlaying(false);

      // Set new selected recording
      setSelectedConversation(conversation);
      setShowModal(true);
      setAudioLoading(true);

      // Clean up previous URL if exists
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }

      // Only download new audio if it's not already loaded
      if (!audioLoaded || selectedConversation?.id !== conversation.id) {
        // Download new audio
        const audioBlob = await downloadAudioFile(
          conversation.recording_filename
        );

        if (!audioBlob) {
          throw new Error(
            "Failed to download audio file - received empty response"
          );
        }

        // Create new URL from blob
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setAudioLoaded(true);
      }

      setAudioLoading(false);

      // Short delay before playing to let audio element initialize
      setTimeout(() => {
        setIsPlaying(true);
      }, 100);
    } catch (err: any) {
      console.error("Error loading audio:", err);
      setAudioError(
        err.message ||
          "Unable to load audio file. The file may be missing or corrupted."
      );
      setAudioLoading(false);
      setAudioLoaded(false);
      setAudioUrl(null);
    }
  };

  // Handle download button click
  const handleDownload = async (conversation: Conversation) => {
    if (!conversation.recording_filename) {
      setError("No recording available for this conversation");
      return;
    }

    try {
      setError(null);
      const audioBlob = await downloadAudioFile(
        conversation.recording_filename
      );

      if (!audioBlob) {
        throw new Error(
          "Failed to download audio file - received empty response"
        );
      }

      // Create download link
      const url = URL.createObjectURL(audioBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", conversation.recording_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error downloading audio:", err);
      setError(
        err.message ||
          "Unable to download audio file. The file may be missing or corrupted."
      );
    }
  };

  // Handle in-modal play toggle without making API calls
  const togglePlayState = () => {
    setIsPlaying(!isPlaying);
  };

  // Close modal and stop playback
  const closeModal = () => {
    setIsPlaying(false);
    setShowModal(false);

    // Clean up audio resources when closing
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setAudioLoaded(false);
  };

  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return date.toLocaleString();
  };

  // Copy Lead code to clipboard function
  const copyLeadToClipboard = (leadCode: string) => {
    navigator.clipboard.writeText(leadCode).then(
      () => {
        setCopiedLead(leadCode);
        // Clear the "Copied" status after 2 seconds
        setTimeout(() => {
          setCopiedLead(null);
        }, 2000);
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  // Function to copy token with animation
  const copyTokenToClipboard = (token: string) => {
    navigator.clipboard.writeText(token).then(
      () => {
        setCopiedToken(token);
        // Clear the animation after 1.5 seconds
        setTimeout(() => {
          setCopiedToken(null);
        }, 1500);
      },
      (err) => {
        console.error("Could not copy token: ", err);
        setError("Failed to copy token to clipboard");
      }
    );
  };

  // Table cell showing Lead and usage info
  const renderLeadUsageCell = (lead: Lead) => (
    <td className="px-4 sm:px-6 py-4">
      <div className="flex flex-col">
        <span className="text-xs sm:text-sm text-[var(--foreground-muted)] mb-1">
          <span className="font-semibold">Uses:</span> {lead.remaining_uses} /{" "}
          {lead.max_uses}
        </span>
        {lead.code && (
          <div className="flex items-center mb-2">
            <div className="bg-[var(--muted)] p-1.5 sm:p-2 rounded-md flex items-center justify-between w-full">
              <span className="text-xs sm:text-md font-mono font-semibold text-[var(--accent)] truncate max-w-[80px] sm:max-w-[120px] md:max-w-none">
                {lead.code}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  copyLeadToClipboard(lead.code);
                }}
                className="ml-1 sm:ml-2 text-[var(--foreground-muted)] hover:text-[var(--accent)] flex-shrink-0"
                title="Copy Lead"
              >
                <FontAwesomeIcon
                  icon={faCopy}
                  className={`${
                    copiedLead === lead.code ? "text-[var(--success)]" : ""
                  }`}
                />
                {copiedLead === lead.code && (
                  <span className="text-xs ml-1 text-[var(--success)] hidden sm:inline">
                    Copied!
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
        <div className="text-xs text-[var(--foreground-muted)] mt-1">
          Expires: {formatDateTime(lead.expires_at)}
        </div>
        <div className="text-xs text-[var(--foreground-muted)] mt-1">
          Conversations: {lead.conversations.length}
        </div>
      </div>
    </td>
  );

  if (loading) {
    return (
      <div className="container mx-auto py-6 sm:py-8 px-3 sm:px-4">
        <LoadingState message="Loading leads..." fullPage={true} />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 sm:py-8 px-3 sm:px-4">
      <style dangerouslySetInnerHTML={{ __html: copiedTooltipStyle }} />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-[var(--foreground)]">
          Lead Management
        </h1>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCreateUserModal(true)}
            className="bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-[var(--accent-foreground)] font-medium py-2 px-3 sm:px-4 rounded-md shadow-sm flex items-center text-sm sm:text-base"
          >
            <HiUserAdd className="mr-1 sm:mr-2" />
            Create Lead
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm mb-4">
          {error}
          <button
            className="ml-2 text-red-700 hover:text-red-900"
            onClick={() => setError(null)}
          >
            ✕
          </button>
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
        <div className="text-sm text-[var(--foreground-muted)] w-full sm:w-auto text-center sm:text-left">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1
                ? "bg-[var(--muted)] text-[var(--foreground-muted)] cursor-not-allowed"
                : "bg-[var(--muted)] text-[var(--foreground)] hover:bg-[var(--muted)]/80"
            }`}
          >
            &laquo;
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages
                ? "bg-[var(--muted)] text-[var(--foreground-muted)] cursor-not-allowed"
                : "bg-[var(--muted)] text-[var(--foreground)] hover:bg-[var(--muted)]/80"
            }`}
          >
            &raquo;
          </button>
        </div>
      </div>

      <div className="bg-[var(--card-container)] shadow-md rounded-lg overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[var(--border)]">
            <thead className="bg-[var(--muted)]">
              <tr>
                <th
                  scope="col"
                  className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider"
                >
                  Lead Information
                </th>
                <th
                  scope="col"
                  className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider"
                >
                  Lead Details
                </th>
                <th
                  scope="col"
                  className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--card)] divide-y divide-[var(--border)]">
              {leads.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-4 text-center text-sm text-[var(--foreground-muted)]"
                  >
                    <div className="flex flex-col items-center justify-center py-12">
                      <img
                        src="/assets/no-conversations.png"
                        alt="No leads"
                        className="w-44 h-auto mb-6"
                      />
                      <h3 className="text-2xl font-bold text-[var(--foreground)] mb-4">
                        No Leads Yet
                      </h3>
                      <p className="text-[var(--foreground-muted)] text-lg max-w-lg mx-auto mb-8">
                        There are currently no leads in the system.
                      </p>
                      <button
                        onClick={() => (window.location.href = "/")}
                        className="px-6 py-3 bg-[var(--accent)] text-white rounded-md hover:bg-[var(--accent-hover)] transition-colors font-semibold"
                      >
                        Back to Home
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <React.Fragment key={lead.id}>
                    <tr
                      className={`hover:bg-[var(--background)]/50 cursor-pointer`}
                      onClick={() => toggleLeadExpand(lead.id)}
                    >
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-[var(--foreground)]">
                            {lead.fullname || "Not provided"}
                          </div>
                          <div className="flex items-center text-xs sm:text-sm text-[var(--foreground-muted)] mt-1 break-all">
                            <HiMail className="mr-1 flex-shrink-0" />
                            <span className="truncate max-w-[150px] sm:max-w-[200px] md:max-w-none">
                              {lead.email || "Not provided"}
                            </span>
                          </div>
                          <div className="flex items-center text-xs sm:text-sm text-[var(--foreground-muted)] mt-1">
                            <HiOutlinePhone className="mr-1 flex-shrink-0" />
                            {lead.phone || "Not provided"}
                          </div>
                          <div className="flex items-center text-xs sm:text-sm text-[var(--foreground-muted)] mt-1">
                            <HiOfficeBuilding className="mr-1 flex-shrink-0" />
                            <span className="truncate max-w-[150px] sm:max-w-[200px] md:max-w-none">
                              {lead.company || "Not provided"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-xs sm:text-sm text-[var(--foreground-muted)] mb-1">
                            <span className="font-semibold">Uses:</span>{" "}
                            {lead.remaining_uses} / {lead.max_uses}
                          </span>
                          {lead.code && (
                            <div className="flex items-center mb-2">
                              <div className="bg-[var(--muted)] p-1.5 sm:p-2 rounded-md flex items-center justify-between w-full">
                                <span className="text-xs sm:text-md font-mono font-semibold text-[var(--accent)] truncate max-w-[80px] sm:max-w-[120px] md:max-w-none">
                                  {lead.code}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyLeadToClipboard(lead.code);
                                  }}
                                  className="ml-1 sm:ml-2 text-[var(--foreground-muted)] hover:text-[var(--accent)] flex-shrink-0"
                                  title="Copy Lead"
                                >
                                  <FontAwesomeIcon
                                    icon={faCopy}
                                    className={`${
                                      copiedLead === lead.code
                                        ? "text-[var(--success)]"
                                        : ""
                                    }`}
                                  />
                                  {copiedLead === lead.code && (
                                    <span className="text-xs ml-1 text-[var(--success)] hidden sm:inline">
                                      Copied!
                                    </span>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="text-xs text-[var(--foreground-muted)] mt-1">
                            Expires: {formatDateTime(lead.expires_at)}
                          </div>
                          <div className="text-xs text-[var(--foreground-muted)] mt-1">
                            Conversations: {lead.conversations.length}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateClick(
                                lead.id.toString(),
                                lead.max_uses
                              );
                            }}
                            className="text-[var(--accent)] hover:text-[var(--accent-hover)]"
                            aria-label="Edit"
                          >
                            <HiOutlinePencil className="inline-block h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAgentAccess(lead.id);
                            }}
                            className={`text-[var(--accent)] hover:text-[var(--accent-hover)] p-1.5 rounded-full transition-colors ${
                              showAgentAccess === lead.id
                                ? "bg-[var(--accent)]/10 text-[var(--accent)]"
                                : "hover:bg-[var(--muted)]"
                            }`}
                            aria-label="Manage Agent Access"
                            title="Manage Agent Access"
                          >
                            <HiLockClosed className="inline-block h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteLeadModal(lead.id.toString());
                            }}
                            className="text-[var(--destructive)] hover:text-red-800"
                            aria-label="Delete"
                          >
                            <HiOutlineTrash className="inline-block h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedLead === lead.id && (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 sm:px-6 py-4 bg-[var(--card)]"
                        >
                          <div className="rounded-md bg-[var(--card)] p-3 sm:p-4 border border-[var(--border)]">
                            <h3 className="text-md font-semibold text-[var(--foreground)] mb-2">
                              Conversations ({lead.conversations.length})
                            </h3>
                            {lead.conversations.length === 0 ? (
                              <p className="text-sm text-[var(--foreground-muted)]">
                                No conversations yet
                              </p>
                            ) : (
                              <div className="space-y-3 max-h-96 overflow-y-auto">
                                {lead.conversations.map(
                                  (conv: Conversation) => {
                                    const convId = generateConversationId(conv);
                                    return (
                                      <div
                                        key={convId}
                                        className={`bg-[var(--card-container)] rounded-md shadow-sm border ${
                                          conv.pinned
                                            ? "border-[var(--accent)] shadow-md"
                                            : "border-[var(--border)]"
                                        } hover:shadow-md transition-all duration-300`}
                                      >
                                        <div
                                          className="p-3 cursor-pointer flex flex-wrap sm:flex-nowrap justify-between items-center hover:bg-[var(--background)]/50 transition-colors"
                                          onClick={() =>
                                            toggleConversationExpand(convId)
                                          }
                                        >
                                          <div className="w-full sm:w-auto mb-2 sm:mb-0">
                                            <div className="flex items-center">
                                              {conv.pinned && (
                                                <span
                                                  className="mr-2 text-[var(--accent)]"
                                                  title="Pinned conversation"
                                                >
                                                  <HiStar className="w-4 h-4" />
                                                </span>
                                              )}
                                              <h4 className="font-medium text-[var(--foreground)] truncate max-w-[200px] sm:max-w-none">
                                                {conv.agent_name ||
                                                  conv.agent?.name ||
                                                  `Agent #${conv.agent_id}`}
                                              </h4>
                                            </div>
                                            <p className="text-xs text-[var(--foreground-muted)]">
                                              {formatDateTime(conv.timestamp)}
                                            </p>
                                            {conv.call_number && (
                                              <p className="text-xs text-[var(--foreground-muted)]">
                                                Phone: {conv.call_number}
                                              </p>
                                            )}
                                          </div>
                                          <div className="flex items-center space-x-1 sm:space-x-2 w-full sm:w-auto justify-end">
                                            {conv.recording_filename && (
                                              <>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePlay(conv);
                                                  }}
                                                  className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                                                  title="Play recording"
                                                >
                                                  <HiPlay className="w-4 h-4" />
                                                </button>

                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownload(conv);
                                                  }}
                                                  className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                                                  title="Download recording"
                                                >
                                                  <HiDownload className="w-4 h-4" />
                                                </button>
                                              </>
                                            )}

                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePinConversation(
                                                  convId,
                                                  !!conv.pinned
                                                );
                                              }}
                                              className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                                              title={
                                                conv.pinned
                                                  ? "Unpin conversation"
                                                  : "Pin conversation"
                                              }
                                            >
                                              {conv.pinned ? (
                                                <HiStar className="w-4 h-4 text-[var(--accent)]" />
                                              ) : (
                                                <HiOutlineStar className="w-4 h-4" />
                                              )}
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openDeleteConversationModal(
                                                  convId
                                                );
                                              }}
                                              className="text-[var(--foreground-muted)] hover:text-[var(--destructive)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                                              title="Delete conversation"
                                            >
                                              <HiOutlineTrash className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </div>

                                        {expandedConversation === convId &&
                                          conv.chat_history && (
                                            <div className="p-3 pt-0 border-t border-[var(--border)]">
                                              <div className="mt-2 space-y-3 max-h-80 overflow-y-auto p-2">
                                                {conv.chat_history.map(
                                                  (msg, i) => (
                                                    <div
                                                      key={i}
                                                      className={`p-2 rounded-lg max-w-[85%] ${
                                                        msg.role === "assistant"
                                                          ? "chat-message assistant"
                                                          : "chat-message user"
                                                      }`}
                                                    >
                                                      <p className="text-sm">
                                                        {msg.content}
                                                      </p>
                                                      <p className="message-sender">
                                                        {msg.role ===
                                                        "assistant"
                                                          ? "Agent"
                                                          : "User"}
                                                      </p>
                                                    </div>
                                                  )
                                                )}
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Agent Access Section */}
                    {showAgentAccess === lead.id && (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 sm:px-6 py-4 bg-[var(--card)]"
                        >
                          <div className="rounded-md bg-[var(--card)] p-3 sm:p-4 border border-[var(--border)]">
                            <div className="flex justify-between items-center mb-3">
                              <h3 className="text-md font-semibold text-[var(--foreground)]">
                                Agent Access Management
                              </h3>
                              <button
                                onClick={() => {
                                  void openAddAgentModal(lead.id);
                                }}
                                className="bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-[var(--accent-foreground)] font-medium py-1.5 px-3 rounded-md shadow-sm flex items-center text-sm"
                              >
                                <HiPlus className="mr-1" />
                                Add Agent Access
                              </button>
                            </div>

                            {!leadAgents[lead.id] ? (
                              <LoadingState
                                message="Loading agent access..."
                                fullPage={false}
                              />
                            ) : leadAgents[lead.id].length === 0 ? (
                              <p className="text-sm text-[var(--foreground-muted)]">
                                No agent access configured for this lead
                              </p>
                            ) : (
                              <div className="bg-[var(--card-container)] rounded-md overflow-hidden">
                                <table className="min-w-full divide-y divide-[var(--border)]">
                                  <thead className="bg-[var(--muted)]">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                                        Agent
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                                        Description
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                                        Access Token
                                      </th>
                                      <th className="px-4 py-2 text-right text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-[var(--border)]">
                                    {leadAgents[lead.id].map((agent) => (
                                      <tr
                                        key={agent.uuid}
                                        className="hover:bg-[var(--background)]/30"
                                      >
                                        <td className="px-4 py-3 text-sm text-[var(--foreground)]">
                                          {agent.name}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[var(--foreground-muted)]">
                                          <div className="truncate max-w-[200px]">
                                            {agent.description}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          {agent.access_token ? (
                                            <div className="flex items-center">
                                              <span className="font-mono text-xs bg-[var(--muted)] px-2 py-1 rounded truncate max-w-[120px]">
                                                {agent.access_token}
                                              </span>
                                              <div className="relative ml-2">
                                                <button
                                                  onClick={() =>
                                                    copyTokenToClipboard(
                                                      agent.access_token || ""
                                                    )
                                                  }
                                                  className={`text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] hover:bg-[var(--muted)]/80 p-1.5 rounded-full transition-all ${
                                                    copiedToken ===
                                                    agent.access_token
                                                      ? "scale-110 text-[var(--success)] bg-[var(--success)]/10"
                                                      : ""
                                                  }`}
                                                  title="Copy token"
                                                >
                                                  <FontAwesomeIcon
                                                    icon={faCopy}
                                                    className="w-3 h-3"
                                                  />
                                                </button>
                                                {copiedToken ===
                                                  agent.access_token && (
                                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-[var(--success)] text-white text-xs py-1 px-2 rounded opacity-100 copied-tooltip">
                                                    Copied!
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          ) : (
                                            <span className="text-[var(--foreground-muted)] text-xs">
                                              No token
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right">
                                          <button
                                            onClick={(e) =>
                                              setConfirmRemoveAgentId(
                                                agent.uuid
                                              )
                                            }
                                            className="text-white bg-[var(--destructive)] hover:bg-[var(--destructive)]/90 p-1.5 rounded-full transition-colors"
                                            title="Remove access"
                                          >
                                            <FontAwesomeIcon
                                              icon={faUnlink}
                                              size="sm"
                                            />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--card)] rounded-lg shadow-lg w-full max-w-md mx-auto">
            <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
              <h3 className="text-xl font-semibold text-[var(--foreground)]">
                Create New Lead
              </h3>
              <button
                onClick={() => setShowCreateUserModal(false)}
                className="text-[var(--foreground-muted)] hover:text-[var(--foreground)] transition-colors"
              >
                <HiX className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateUser();
                }}
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="fullname"
                    value={newUser.fullname}
                    onChange={handleUserInputChange}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-md bg-[var(--background)] text-[var(--foreground)] placeholder-[var(--foreground-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)]"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={newUser.email}
                    onChange={handleUserInputChange}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-md bg-[var(--background)] text-[var(--foreground)] placeholder-[var(--foreground-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)]"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={newUser.phone}
                    onChange={handleUserInputChange}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-md bg-[var(--background)] text-[var(--foreground)] placeholder-[var(--foreground-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)]"
                    placeholder="+1234567890"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={newUser.company}
                    onChange={handleUserInputChange}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-md bg-[var(--background)] text-[var(--foreground)] placeholder-[var(--foreground-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)]"
                    placeholder="CAW Studios"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
                    Maximum Uses
                  </label>
                  <input
                    type="number"
                    name="max_uses"
                    min="1"
                    value={newUser.max_uses}
                    onChange={handleUserInputChange}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-md bg-[var(--background)] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)]"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateUserModal(false)}
                    className="px-5 py-2.5 bg-[var(--muted)] text-[var(--foreground)] rounded-md hover:bg-[var(--muted)]/80 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-[var(--accent)] text-white rounded-md hover:bg-[var(--accent)]/90 transition-colors font-medium"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--card)] rounded-lg shadow-lg w-full max-w-md mx-auto">
            <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
              <h3 className="text-xl font-semibold text-[var(--foreground)]">
                Update Lead
              </h3>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-[var(--foreground-muted)] hover:text-[var(--foreground)] transition-colors"
              >
                <HiX className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label
                  htmlFor="maxUses"
                  className="block text-sm font-medium text-[var(--foreground)] mb-1.5"
                >
                  Maximum Uses
                </label>
                <input
                  type="number"
                  id="maxUses"
                  min="1"
                  value={maxUses}
                  onChange={(e) =>
                    setMaxUses(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="w-full px-4 py-2.5 border border-[var(--border)] rounded-md bg-[var(--background)] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)]"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowUpdateModal(false)}
                  className="px-5 py-2.5 bg-[var(--muted)] text-[var(--foreground)] rounded-md hover:bg-[var(--muted)]/80 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdate}
                  className="px-5 py-2.5 bg-[var(--accent)] text-white rounded-md hover:bg-[var(--accent)]/90 transition-colors font-medium"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal with audio visualizer */}
      {showModal && selectedConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-container)] rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-4 border-b border-[var(--border)] flex justify-between items-center">
              <h2 className="text-lg font-medium text-[var(--foreground)]">
                {selectedConversation.agent_name ||
                  selectedConversation.agent?.name ||
                  `Agent #${selectedConversation.agent_id}`}
                <span className="text-sm text-[var(--foreground-muted)] ml-2">
                  {formatDateTime(selectedConversation.timestamp)}
                </span>
                {selectedConversation.call_number && (
                  <div className="text-sm text-[var(--foreground-muted)]">
                    Phone: {selectedConversation.call_number}
                  </div>
                )}
              </h2>
              <button
                className="text-[var(--foreground-muted)] hover:text-[var(--foreground)]"
                onClick={closeModal}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6 bg-[var(--card-container)] rounded-b-lg">
              {audioLoading ? (
                <div className="flex items-center justify-center py-10">
                  <LoadingState
                    size="medium"
                    padding="none"
                    message="Loading audio..."
                    fullPage={false}
                  />
                </div>
              ) : audioError ? (
                <div className="text-center py-8">
                  <p className="text-[var(--destructive)]">{audioError}</p>
                </div>
              ) : (
                audioUrl && (
                  <StereoAudioVisualizer
                    audioUrl={audioUrl}
                    isPlaying={isPlaying}
                    onEnded={handleAudioEnded}
                  />
                )
              )}

              <div className="flex justify-center items-center space-x-4 mt-6">
                <button
                  className="rounded-full w-12 h-12 flex items-center justify-center bg-[var(--accent)] text-[var(--accent-foreground)] hover:opacity-90 transition-colors"
                  onClick={togglePlayState}
                  disabled={loading || !audioUrl || Boolean(audioError)}
                >
                  <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                </button>

                <button
                  className="rounded-full w-12 h-12 flex items-center justify-center bg-[var(--accent)] text-[var(--accent-foreground)] hover:opacity-90 transition-colors ml-4"
                  onClick={() =>
                    selectedConversation && handleDownload(selectedConversation)
                  }
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Lead Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteLeadModal}
        title="Delete Lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => handleDelete(leadToDelete)}
        onCancel={() => setShowDeleteLeadModal(false)}
        isDestructive={true}
      />

      {/* Delete Conversation Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConversationModal}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => handleDeleteConversation(conversationToDelete)}
        onCancel={() => setShowDeleteConversationModal(false)}
        isDestructive={true}
      />

      {/* Add Agent Access Modal */}
      {showAddAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--card)] rounded-lg shadow-lg w-full max-w-md mx-auto">
            <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
              <h3 className="text-xl font-semibold text-[var(--foreground)]">
                Add Agent Access
              </h3>
              <button
                onClick={() => setShowAddAgentModal(false)}
                className="text-[var(--foreground-muted)] hover:text-[var(--foreground)] transition-colors"
              >
                <HiX className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              {loadingAgents ? (
                <LoadingState message="Loading agents..." fullPage={false} />
              ) : (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
                      Select Agent
                    </label>
                    <select
                      value={selectedAgentUuid}
                      onChange={(e) => setSelectedAgentUuid(e.target.value)}
                      className="w-full px-4 py-2.5 border border-[var(--border)] rounded-md bg-[var(--background)] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)]"
                    >
                      <option value="">-- Select an agent --</option>
                      {availableAgents.map((agent) => (
                        <option key={agent.uuid} value={agent.uuid}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAddAgentModal(false)}
                      className="px-5 py-2.5 bg-[var(--muted)] text-[var(--foreground)] rounded-md hover:bg-[var(--muted)]/80 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddAgentAccess}
                      disabled={!selectedAgentUuid}
                      className={`px-5 py-2.5 rounded-md font-medium ${
                        selectedAgentUuid
                          ? "bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90"
                          : "bg-[var(--accent)]/50 text-white cursor-not-allowed"
                      }`}
                    >
                      Add Access
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remove Agent Access Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!confirmRemoveAgentId}
        title="Remove Agent Access"
        message="Are you sure you want to remove access for this agent? This action cannot be undone."
        confirmText="Remove Access"
        cancelText="Cancel"
        onConfirm={() => {
          if (confirmRemoveAgentId && showAgentAccess) {
            handleRemoveAgentAccess(showAgentAccess, confirmRemoveAgentId);
          }
        }}
        onCancel={() => setConfirmRemoveAgentId(null)}
        isDestructive={true}
      />
    </div>
  );
};

export default Leads;
